import { useState, useEffect } from "react";
import { Loader2, ChevronUp, ChevronDown, X, Plus } from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import { Button } from "../../components/ui";
import {
  fetchSystemSettings,
  updateSystemSettings,
} from "../../services/api";

export default function MasterListSection({ title, description, icon: Icon, settingsKey, defaults }) {
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    fetchSystemSettings()
      .then((s) => setItems(s[settingsKey] || defaults || []))
      .catch(() => setItems(defaults || []))
      .finally(() => setLoading(false));
  }, []);

  const save = async (updated) => {
    setSaving(true);
    setStatus(null);
    try {
      await updateSystemSettings({ [settingsKey]: updated });
      setItems(updated);
      setStatus("saved");
      setTimeout(() => setStatus(null), 2000);
    } catch {
      setStatus("error");
    } finally {
      setSaving(false);
    }
  };

  const addItem = () => {
    const val = newItem.trim();
    if (!val || items.some((i) => i.toLowerCase() === val.toLowerCase())) return;
    save([...items, val]);
    setNewItem("");
  };

  const removeItem = (idx) => {
    save(items.filter((_, i) => i !== idx));
  };

  const moveItem = (idx, dir) => {
    const next = [...items];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    save(next);
  };

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: 24,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <Icon size={18} color={colors.accent} />
        <h3 style={{ fontFamily: fonts.heading, fontSize: 16, color: colors.text, margin: 0 }}>
          {title}
        </h3>
        {saving && <Loader2 size={14} className="spin" color={colors.textSecondary} />}
        {status === "saved" && <span style={{ fontSize: 11, color: colors.success, fontWeight: 600 }}>Saved</span>}
        {status === "error" && <span style={{ fontSize: 11, color: colors.danger, fontWeight: 600 }}>Save failed</span>}
      </div>
      <p style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 16, lineHeight: 1.5 }}>
        {description}
      </p>

      {loading ? (
        <div style={{ textAlign: "center", padding: 20, color: colors.textSecondary }}>
          <Loader2 size={16} className="spin" />
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 12 }}>
            {items.map((item, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 10px",
                  background: colors.bg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 6,
                  fontSize: 13,
                  color: colors.text,
                }}
              >
                <span style={{ flex: 1 }}>{item}</span>
                <button
                  onClick={() => moveItem(idx, -1)}
                  disabled={idx === 0}
                  title="Move up"
                  style={{ background: "none", border: "none", cursor: idx === 0 ? "default" : "pointer", padding: 2, display: "flex", color: idx === 0 ? colors.border : colors.textSecondary }}
                >
                  <ChevronUp size={13} />
                </button>
                <button
                  onClick={() => moveItem(idx, 1)}
                  disabled={idx === items.length - 1}
                  title="Move down"
                  style={{ background: "none", border: "none", cursor: idx === items.length - 1 ? "default" : "pointer", padding: 2, display: "flex", color: idx === items.length - 1 ? colors.border : colors.textSecondary }}
                >
                  <ChevronDown size={13} />
                </button>
                <button
                  onClick={() => removeItem(idx)}
                  title="Remove"
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex", color: colors.danger }}
                >
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addItem(); }}
              placeholder={`Add new ${title.toLowerCase().replace(/s$/, "")}...`}
              style={{
                flex: 1,
                padding: "8px 12px",
                fontSize: 13,
                border: `1px solid ${colors.border}`,
                borderRadius: 6,
                background: colors.bg,
                color: colors.text,
                outline: "none",
                fontFamily: fonts.body,
              }}
            />
            <Button onClick={addItem} disabled={!newItem.trim()} style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Plus size={14} /> Add
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
